import { api } from './api';
import type { PublicProfileCompanyChecklist } from './public-profile-builder';
import type {
  PublicProfileAnalyticsRange,
  PublicProfileAnalyticsSummary,
  PublicProfileBuilderState,
  PublicProfileEventIngestInput,
  PublicProfileMode,
  PublicProfilePublicCompanyPage,
  UpdatePublicProfileBlocksInput,
  UpdatePublicProfileContactsInput,
  UpdatePublicProfileDesignInput,
  UpdatePublicProfileSettingsInput,
  UpdatePublicProfileSocialsInput,
  UpdatePublicProfileTeamInput,
  CvViewingPrivacy,
  PublicProfileAnalyticsActorDetail,
  PublicProfileAnalyticsActorKind,
} from '@tabbio/types';

type BuilderQueryParams = {
  mode: PublicProfileMode;
  range?: PublicProfileAnalyticsRange;
};

function withQueryParams(path: string, params: Record<string, string | undefined>) {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value && value.trim()) {
      searchParams.set(key, value);
    }
  }
  const qs = searchParams.toString();
  return qs ? `${path}?${qs}` : path;
}

function normalizePublicProfileErrorMessage(
  message: string | undefined,
  fallback: string,
) {
  const trimmed = typeof message === 'string' ? message.trim() : '';
  if (!trimmed) {
    return fallback;
  }

  if (
    trimmed.length > 220 ||
    trimmed.includes('/Users/') ||
    trimmed.includes('Invalid `') ||
    trimmed.includes('invocation in') ||
    trimmed.includes('does not exist in the current database')
  ) {
    return fallback;
  }

  return trimmed;
}

function unwrapBuilderResponse(
  res: { data?: PublicProfileBuilderState | null; error?: { message: string } | null },
  fallbackMessage: string,
) {
  if (res.error) {
    throw new Error(normalizePublicProfileErrorMessage(res.error.message, fallbackMessage));
  }
  if (!res.data) {
    throw new Error(fallbackMessage);
  }
  return res.data;
}

export async function fetchPublicProfileBuilderState(
  params: BuilderQueryParams,
): Promise<PublicProfileBuilderState> {
  const res = await api.get<PublicProfileBuilderState>(
    withQueryParams('/api/public-profiles/builder', {
      mode: params.mode,
      range: params.range,
    }),
  );
  return unwrapBuilderResponse(res, 'Failed to load public profile builder');
}

/**
 * Lightweight analytics fetch for the CV analytics modal + screen: hits the
 * dedicated `builder/analytics` endpoint instead of re-loading the full builder
 * state. Returns the public-profile reach summary (views/visitors/shares +
 * source/series breakdowns). Range-aware (7d / 30d / 90d / all) and,
 * with `cvId`, scoped to one CV.
 */
export async function fetchPublicProfileAnalytics(params: {
  mode: PublicProfileMode;
  range: PublicProfileAnalyticsRange;
  cvId?: string | null;
}): Promise<PublicProfileAnalyticsSummary> {
  const res = await api.get<PublicProfileAnalyticsSummary>(
    withQueryParams('/api/public-profiles/builder/analytics', {
      mode: params.mode,
      range: params.range,
      cvId: params.cvId ?? undefined,
    }),
  );
  if (res.error) {
    throw new Error(
      normalizePublicProfileErrorMessage(res.error.message, 'Failed to load analytics'),
    );
  }
  if (!res.data) {
    throw new Error('Failed to load analytics');
  }
  return res.data;
}

/**
 * The activity behind a feed row is no longer there: it was removed, or the
 * member now views CVs privately. Retrying cannot bring it back.
 */
export class AnalyticsActivityGoneError extends Error {
  constructor(message = 'This activity is no longer available') {
    super(message);
    this.name = 'AnalyticsActivityGoneError';
  }
}

/**
 * One actor's activity (a member, a company, or the anonymous visitors as a
 * group) in the analytics page's scope. Premium only: the API refuses Free.
 */
export async function fetchPublicProfileAnalyticsActor(params: {
  range: PublicProfileAnalyticsRange;
  cvId?: string | null;
  kind: PublicProfileAnalyticsActorKind;
  id?: string | null;
}): Promise<PublicProfileAnalyticsActorDetail> {
  const res = await api.get<PublicProfileAnalyticsActorDetail>(
    withQueryParams('/api/public-profiles/builder/analytics/actor', {
      mode: 'individual',
      range: params.range,
      cvId: params.cvId ?? undefined,
      kind: params.kind,
      id: params.id ?? undefined,
    }),
  );
  if (res.error?.code === 'ANALYTICS_ACTIVITY_NOT_FOUND') {
    throw new AnalyticsActivityGoneError(res.error.message);
  }
  if (res.error || !res.data) {
    throw new Error(
      normalizePublicProfileErrorMessage(res.error?.message, 'Failed to load activity details'),
    );
  }
  return res.data;
}

/** Whether this member's views of other people's CVs are recorded anonymously. */
export async function fetchCvViewingPrivacy(): Promise<CvViewingPrivacy> {
  const res = await api.get<CvViewingPrivacy>('/api/public-profiles/viewing-privacy');
  if (res.error || !res.data) {
    throw new Error(res.error?.message || 'Failed to load viewing privacy');
  }
  return res.data;
}

export async function updateCvViewingPrivacy(
  input: CvViewingPrivacy,
): Promise<CvViewingPrivacy> {
  const res = await api.put<CvViewingPrivacy>('/api/public-profiles/viewing-privacy', input);
  if (res.error || !res.data) {
    throw new Error(res.error?.message || 'Failed to save viewing privacy');
  }
  return res.data;
}

export async function updatePublicProfileBlocks(
  input: UpdatePublicProfileBlocksInput,
): Promise<PublicProfileBuilderState> {
  const res = await api.patch<PublicProfileBuilderState>('/api/public-profiles/builder/blocks', input);
  return unwrapBuilderResponse(res, 'Failed to update public profile blocks');
}

export async function updatePublicProfileSocials(
  input: UpdatePublicProfileSocialsInput,
): Promise<PublicProfileBuilderState> {
  const res = await api.patch<PublicProfileBuilderState>('/api/public-profiles/builder/socials', input);
  return unwrapBuilderResponse(res, 'Failed to update public profile socials');
}

export async function updatePublicProfileContacts(
  input: UpdatePublicProfileContactsInput,
): Promise<PublicProfileBuilderState> {
  const res = await api.patch<PublicProfileBuilderState>('/api/public-profiles/builder/contacts', input);
  return unwrapBuilderResponse(res, 'Failed to update public profile contacts');
}

export async function updatePublicProfileDesign(
  input: UpdatePublicProfileDesignInput,
): Promise<PublicProfileBuilderState> {
  const res = await api.patch<PublicProfileBuilderState>('/api/public-profiles/builder/design', input);
  return unwrapBuilderResponse(res, 'Failed to update public profile design');
}

export async function updatePublicProfileSettings(
  input: UpdatePublicProfileSettingsInput,
): Promise<PublicProfileBuilderState> {
  const res = await api.patch<PublicProfileBuilderState>('/api/public-profiles/builder/settings', input);
  return unwrapBuilderResponse(res, 'Failed to update public profile settings');
}

export async function updatePublicProfileTeam(
  input: UpdatePublicProfileTeamInput,
): Promise<PublicProfileBuilderState> {
  const res = await api.patch<PublicProfileBuilderState>('/api/public-profiles/builder/team', input);
  return unwrapBuilderResponse(res, 'Failed to update public profile team');
}

/**
 * Server-truth share confirmation (PRD §13): stamps the persisted `shared`
 * checklist outcome after the public page resolved. Returns the recomputed
 * company checklist. Callers tolerate failure silently — the share action
 * itself already succeeded and must not surface an error for bookkeeping.
 */
export async function confirmPublicProfileShare(input: {
  mode: PublicProfileMode;
}): Promise<{ checklist: PublicProfileCompanyChecklist }> {
  const res = await api.post<{ checklist: PublicProfileCompanyChecklist }>(
    '/api/public-profiles/builder/share-confirm',
    input,
  );
  if (res.error) {
    throw new Error(
      normalizePublicProfileErrorMessage(res.error.message, 'Failed to confirm share'),
    );
  }
  if (!res.data) {
    throw new Error('Share confirmation returned no data');
  }
  return res.data;
}

export async function trackPublicProfileEvent(
  input: PublicProfileEventIngestInput,
): Promise<{ recorded: boolean }> {
  const res = await api.post<{ recorded: boolean }>('/api/public-profiles/events', input);
  if (res.error) {
    throw new Error(res.error.message);
  }
  if (!res.data) {
    throw new Error('Public profile event tracking returned no data');
  }
  return res.data;
}

/** The live public company page (name/logo/etc.) for a company slug. */
export async function fetchPublicCompanyPage(
  slug: string,
): Promise<PublicProfilePublicCompanyPage> {
  const res = await api.get<PublicProfilePublicCompanyPage>(
    `/api/public-profiles/companies/${encodeURIComponent(slug)}`,
  );
  if (res.error) {
    throw new Error(
      normalizePublicProfileErrorMessage(
        res.error.message,
        'Failed to load the company profile',
      ),
    );
  }
  if (!res.data) {
    throw new Error('Company public profile not found');
  }
  return res.data;
}
